import { t } from '@lingui/macro'
import clsx from 'clsx'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { Button, Form, Input, SearchField } from 'react-aria-components'

import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { generatePath, useNavigate } from 'react-router-dom'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import SvgSearch from '@jotta/ui/SvgSearch'
import SvgX from '@jotta/ui/SvgX'

const debug = Debug('jotta:FileSearch')

export interface FileSearchProps {
  open: boolean
  setOpen: (open: boolean) => void
}

export const FileSearch = observer<FileSearchProps>(function FileSearch({
  open,
  setOpen,
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const { viewMode, sortBy, context } = useFileRouteParams()
  const navigate = useNavigate()

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    debug(e)
    e.preventDefault()
    if (!open) {
      setOpen(true)

      ref.current!.disabled = false
      ref.current!.focus()
    } else if (query === '') {
      setOpen(false)
    } else {
      doSearch(query)
      setQuery('')
    }
  }

  return (
    <Form
      data-active={open}
      className={clsx(
        'group z-20 flex h-header flex-auto items-center px-4 py-2 transition-all duration-300 sm:items-stretch',
        {
          'relative bottom-0 left-auto right-auto top-0': !open,
          'absolute inset-0 sm:relative': open,
        },
      )}
      onSubmit={onSubmit}
    >
      <SearchField
        isDisabled={!open}
        value={query}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setOpen(false)
            ref.current?.blur()
          }
        }}
        onChange={setQuery}
        onSubmit={doSearch}
        aria-label={t`Search for files`}
        name="query"
        className="pointer-events-none flex h-10 w-0 flex-auto translate-x-full opacity-0 transition-all duration-300 group-data-active:pointer-events-auto group-data-active:translate-x-0 group-data-active:opacity-100"
        onClear={() => setOpen(false)}
      >
        <Input
          placeholder={t`What are you looking for?`}
          ref={ref}
          className="flex-auto rounded-lg pl-6 pr-2 text-primary shadow-activePhotoSearchInput shadow-black/35 ring-sx-primary hideSearchCancelButton focus:ring-2"
        />
        <Button
          aria-label={t`Close search input`}
          className="rounded-lg px-2 ring-sx-primary focus-visible:ring-2"
        >
          <PlainBrandIcon icon={SvgX} className="size-6" />
        </Button>
      </SearchField>
      <Button
        type="submit"
        id="file-search-button"
        aria-label={t`Search for files`}
        aria-haspopup="true"
        aria-expanded={open}
        className="rounded-lg px-2 focus:text-sx-primary focus:ring-0 focus-visible:ring-0"
      >
        <PlainBrandIcon icon={SvgSearch} className="size-6" />
      </Button>
    </Form>
  )

  function doSearch(query: string) {
    if (query) {
      navigate(
        generatePath('/web/search/:viewMode/:sortBy/:query', {
          viewMode,
          sortBy,
          query: encodeURIComponent(query),
        }),
        {
          replace: context === 'search',
        },
      )
    }
  }
})
